import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { Item } from "../../pojo/Item";

export type FieldType =
    | "SELECT"
    | "MULTISELECT"
    | "TEXT"
    | "SECTION"
    | "FILE"
    | "IMAGE"
    | "EVENT_TYPE"
    | "ORGANIZATION"
    | "CONTACT"
    | "ROLE"
    | "ORG_TYPE"
    | "ORG_RATING"
    | "SECURITY_GROUP"
    | "OBJECT_OCCURRENCE"
    | "FORM"
    | "REGISTRANTS"
    | "EVENT"
    | "MULTISELECT_OCCURRENCES"
    | "CHECKBOX"
    | "RADIOBUTTON"
    | "CATEGORIES"
    | "STARS_RATING"
    | "NUMERIC_SCALE"
    | "CUSTOM_ATTRIBUTE"
    | "REQUIREMENTS"
    | "CONTACT_ROLE"
    | "ORGANIZATION_MULTISELECT"
    | "ORG_CATEGORIES"
    | "ORG_CUSTOM_ATTRIBUTE"
    | "ORG_CONTACT_ROLE"
    | "DURATION"
    | "TOGGLE"
    | "RICH_TEXT"
    | "ELECTION_POSITION"
    | "LIVE_25_EVENT_FORM";

interface NameResolvableConfig {
    searchCriteriaType: SearchCriteriaType["type"];
    itemTypeId?: number;
}

/**
 * Field types whose stored value is an ID that needs name resolution for display.
 * Maps each such field type to its search criteria type (for dropdown population)
 * and optional Item type ID (for NameService lookups).
 *
 * This is the single source of truth for "special field type" checks throughout
 * the data-ingestion module. Use `isNameResolvableFieldType()` to check membership.
 */

export const NAME_RESOLVABLE_FIELD_TYPES = {
    EVENT_TYPE: { searchCriteriaType: "eventTypes", itemTypeId: Item.Ids.EventType },
    EVENT: { searchCriteriaType: "events", itemTypeId: Item.Ids.Event },
    ORGANIZATION: { searchCriteriaType: "organizations", itemTypeId: Item.Ids.Organization },
    CONTACT: { searchCriteriaType: "contacts", itemTypeId: Item.Ids.Contact },
    ROLE: { searchCriteriaType: "organizationRoles", itemTypeId: undefined as number | undefined },
    EVENT_ROLE: { searchCriteriaType: "eventRoles", itemTypeId: undefined as number | undefined },
    ORG_TYPE: { searchCriteriaType: "organizationTypes", itemTypeId: undefined as number | undefined },
    ORG_RATING: { searchCriteriaType: "organizationRatings", itemTypeId: undefined as number | undefined },
    SECURITY_GROUP: { searchCriteriaType: "securityGroups", itemTypeId: undefined as number | undefined },
    CATEGORIES: { searchCriteriaType: "eventCategories", itemTypeId: undefined as number | undefined },
    REQUIREMENTS: { searchCriteriaType: "eventRequirements", itemTypeId: undefined as number | undefined },
    ORGANIZATION_MULTISELECT: { searchCriteriaType: "organizations", itemTypeId: undefined as number | undefined },
    ORG_CATEGORIES: { searchCriteriaType: "organizationCategories", itemTypeId: undefined as number | undefined },
    ELECTION_POSITION: { searchCriteriaType: "electionPositions", itemTypeId: undefined as number | undefined },
} as const satisfies Record<string, NameResolvableConfig>;

export type NameResolvableFieldType = keyof typeof NAME_RESOLVABLE_FIELD_TYPES;

export function isNameResolvableFieldType(fieldType?: string): fieldType is NameResolvableFieldType {
    return !!fieldType && fieldType in NAME_RESOLVABLE_FIELD_TYPES;
}

/** Field types that store structured JSON data in `jsonFieldItem`. */
export const JSON_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
    "OBJECT_OCCURRENCE",
    "REGISTRANTS",
    "CONTACT_ROLE",
    "ORG_CONTACT_ROLE",
]);

/** Field types that cannot be used on authless ingestion forms. */
export const INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES = [
    "FILE",
    "IMAGE",
    "EVENT_TYPE",
    "ORGANIZATION",
    "CONTACT",
    "ROLE",
    "ORG_TYPE",
    "ORG_RATING",
    "SECURITY_GROUP",
    "EVENT",
    "CATEGORIES",
    "CUSTOM_ATTRIBUTE",
    "OBJECT_OCCURRENCE",
    "REQUIREMENTS",
    "CONTACT_ROLE",
    "ORGANIZATION_MULTISELECT",
    "ORG_CATEGORIES",
    "ORG_CUSTOM_ATTRIBUTE",
    "ORG_CONTACT_ROLE",
    "LIVE_25_EVENT_FORM",
] as const satisfies readonly FieldType[];

export const INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPE_SET: ReadonlySet<FieldType> = new Set(
    INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPES,
);

export function isAuthlessRestrictedFieldType(fieldType?: string): fieldType is FieldType {
    return !!fieldType && INGESTION_AUTHLESS_RESTRICTED_FIELD_TYPE_SET.has(fieldType as FieldType);
}

/**
 * Field types that use `specialFieldItem` (a single DropDownItem) for their value.
 * In rule evaluation, these return `{ value: [specialFieldItem.itemId], type: "multiselect" }`.
 *
 * Contrast with JSON_MULTISELECT_FIELD_TYPES which store `{ items: { itemId }[] }`
 * in jsonFieldItem and support multiple selections natively.
 */
export const SPECIAL_FIELD_ITEM_TYPES: ReadonlySet<FieldType> = new Set([
    "EVENT_TYPE",
    "ORGANIZATION",
    "ORG_TYPE",
    "ORG_RATING",
    "SECURITY_GROUP",
]);

/**
 * Field types that use `jsonFieldItem` with `{ items: { itemId }[] }` structure
 * for multi-select values. In rule evaluation, these return
 * `{ value: items.map(i => i.itemId), type: "multiselect" }`.
 */
export const JSON_MULTISELECT_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
    "CATEGORIES",
    "REQUIREMENTS",
    "ORGANIZATION_MULTISELECT",
    "ORG_CATEGORIES",
]);

/**
 * Field types that may not be used as a workflow action-input mapping target unless the input spec
 * explicitly lists them in `acceptedFieldTypes`.
 *
 * Governs mapping (AI auto-map, `$CNET-…` references) when an input has no `acceptedFieldTypes` —
 * not whether an action reads the answer at runtime. Types like EVENT_TYPE and ORG_RATING are often
 * auto-collected by scanning the form but remain mappable because they store a single ID like ORGANIZATION.
 *
 * Includes structured/JSON answers, types usually consumed via type-scan on Create Event/Org, and
 * layout/meta fields (SECTION, FORM, EVENT, …). Align with Java
 * `IngestionFieldDefinition.FieldType.REQUIRES_EXPLICIT_ACCEPTED_FIELD_TYPES`.
 */
export const REQUIRES_EXPLICIT_ACCEPTED_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
    ...JSON_MULTISELECT_FIELD_TYPES,
    ...JSON_FIELD_TYPES,
    "CUSTOM_ATTRIBUTE",
    "ORG_CUSTOM_ATTRIBUTE",
    "MULTISELECT_OCCURRENCES",
    "SECTION",
    "FORM",
    "EVENT",
    "LIVE_25_EVENT_FORM",
    "ELECTION_POSITION",
]);

/**
 * Field types whose stored value is directly human-readable -- not an ID
 * needing name resolution, not structured JSON, not a layout/meta field.
 */
export const PRIMITIVE_FIELD_TYPES: ReadonlySet<FieldType> = new Set([
    "TEXT",
    "IMAGE",
    "SELECT",
    "MULTISELECT",
    "CHECKBOX",
    "RADIOBUTTON",
    "TOGGLE",
    "NUMERIC_SCALE",
    "STARS_RATING",
]);

export function isPrimitiveFieldType(fieldType?: string): boolean {
    return !fieldType || PRIMITIVE_FIELD_TYPES.has(fieldType as FieldType);
}

export enum IngestionPointCategory {
    ENGAGEMENT = "ENGAGEMENT",
}

export const INGESTION_CATEGORIES: IngestionPointCategory[] = [IngestionPointCategory.ENGAGEMENT];

export const INGESTION_POINT_FORM_USES = {
    registration: "Registration",
    eventSurvey: "Event Survey",
    eventCreation: "Event Creation",
    eventEdit: "Edit Event",
    orgCreation: "Create Organization",
    orgEdit: "Edit Organization",
    orgJoin: "Join Organization",
    electionCandidate: "Election Candidate",
} as const;

export type IngestionPointFormUseKey = keyof typeof INGESTION_POINT_FORM_USES;

export const INGESTION_POINT_FORM_USE_KEYS: readonly IngestionPointFormUseKey[] = Object.keys(
    INGESTION_POINT_FORM_USES,
) as IngestionPointFormUseKey[];

export type IngestionPointFormUse = Partial<Record<IngestionPointFormUseKey, boolean>>;

const NOT_LISTED = "On its own, not listed in My Forms or on the Forms tab.";

export const FORM_USE_COMBINED_VISIBILITY_HELP =
    "A form with several uses appears wherever any of its checked uses would show it.";

const FORM_USE_VISIBILITY: Record<IngestionPointFormUseKey, string> = {
    registration: `Used when it is selected in an event's registration settings. ${NOT_LISTED}`,
    eventSurvey: `Used when it is selected in an event's survey settings. ${NOT_LISTED}`,
    eventCreation:
        "Shown in My Forms, and on its organization's Forms tab to users who can create events. Used by the organization's Create Event button.",
    eventEdit: `Opened from the event page by people editing the event. ${NOT_LISTED}`,
    orgCreation: "Shown in My Forms and on its organization's Forms tab.",
    orgEdit: "Shown on its organization's Forms tab. Can be opened from a form button added to the organization page.",
    orgJoin: `Used by the organization's Join button. ${NOT_LISTED}`,
    electionCandidate: `Opened from the election nominations page. ${NOT_LISTED}`,
};

/** Where a form appears for each use, shown as help next to the form-use checkboxes. */
export const FORM_USE_VISIBILITY_HELP: readonly { label: string; description: string }[] = [
    {
        label: "No uses selected",
        description: "Shown in My Forms and on the Forms tab of the organization it is scoped to.",
    },
    ...INGESTION_POINT_FORM_USE_KEYS.map((useKey) => ({
        label: INGESTION_POINT_FORM_USES[useKey],
        description: FORM_USE_VISIBILITY[useKey],
    })),
];

export function getActiveFormUses(use?: IngestionPointFormUse): IngestionPointFormUseKey[] {
    if (!use) {
        return [];
    }
    return INGESTION_POINT_FORM_USE_KEYS.filter((useKey) => use[useKey]);
}

export const INGESTION_FIELD_TYPE_ID = 8001;
export const INGESTION_POINT_TYPE_ID = 11_001;

export const INGESTION_SYSTEM_FIELDS = {
    ORGANIZATION: -1,
    EVENT: -2,
    MULTISELECT_OCCURRENCES: -3,
    OBJECT_OCCURRENCE_SNAPSHOT: -4,
} as const;

export const STARS_NUMERIC_SCALE_MIN = 1;
export const STARS_NUMERIC_SCALE_MAX = 20;
